import axios from "axios";

import { DEFAULT_PERMISSIONS, NEW_CONFIG_DEFAULTS } from "./constants";
import { CustomKdsTicketColors, DeviceProfileConfig, DeviceProfileModalData, DeviceProfileSavePayload } from "./types";

const isEmptyObject = (obj: Record<string, unknown> | undefined | null): boolean =>
  !obj || Object.keys(obj).length === 0;

// Seeds the config for the modal, replicating handheldConfigAddEditController.js:121-134:
// backfill permissions with defaults (so new actions appear on old configs), default the
// arrays, and fall back to the computed `preferences` when no explicit default_preferences
// have been saved yet.
export const buildInitialConfig = (config: DeviceProfileConfig | null): DeviceProfileConfig => {
  const base: DeviceProfileConfig = config ? { ...NEW_CONFIG_DEFAULTS, ...config } : { ...NEW_CONFIG_DEFAULTS };

  base.permissions = { ...DEFAULT_PERMISSIONS, ...(base.permissions || {}) };
  base.additional_closed_statuses = base.additional_closed_statuses || [];
  base.statuses_to_chirp_for = base.statuses_to_chirp_for || [];
  base.locations = base.locations || [];
  base.stations = base.stations || [];
  base.kds_stations = base.kds_stations || [];
  base.menus = base.menus || [];
  base.custom_kds_ticket_colors = base.custom_kds_ticket_colors || {};

  if (isEmptyObject(base.default_preferences)) {
    base.default_preferences = base.preferences ? { ...base.preferences } : {};
  }

  return base;
};

// TOGGLE ALL: if everything is already selected, clear the selection; otherwise select all.
export const computeToggleAll = (currentIds: string[], allIds: string[]): string[] =>
  currentIds.length >= allIds.length ? [] : [...allIds];

// Merges the user's edits back over the full original config so keys the dialog never
// renders are preserved: extra scalar columns, and unshown keys inside the permissions /
// default_preferences blobs. The backend does a flat column overwrite (.update()), so any
// key omitted here would be silently wiped. Colors are replaced wholesale because a removal
// must drop the key entirely.
export const mergeEditedConfig = (
  initial: DeviceProfileConfig,
  edited: Partial<DeviceProfileConfig>,
  customColors: CustomKdsTicketColors
): DeviceProfileConfig => ({
  ...initial,
  ...edited,
  permissions: { ...initial.permissions, ...(edited.permissions || {}) },
  default_preferences: { ...initial.default_preferences, ...(edited.default_preferences || {}) },
  custom_kds_ticket_colors: { ...customColors },
});

export const buildSavePayload = (mergedConfig: DeviceProfileConfig, customerId: string): DeviceProfileSavePayload => {
  const payload: Record<string, unknown> = {
    ...mergedConfig,
    customer_id: customerId,
    quick_order_location: mergedConfig.quick_order_location || null,
  };
  // `preferences` is a read-only computed field; never send it back.
  delete payload.preferences;
  // Omitting id tells the backend to create a new profile.
  if (!payload.id) {
    delete payload.id;
  }
  return payload as DeviceProfileSavePayload;
};

// Fetches the dropdown/checklist data the dialog needs on open. The owner-properties call
// supplies the currency used to gate the card-payment toggles (USD only), matching the
// AngularJS controller's allow_cc logic.
export const fetchDeviceProfileModalData = async (customerId: string): Promise<DeviceProfileModalData> => {
  const [stationsRes, locationsRes, menusRes, fulfillmentRes, ownerPropsRes] = await Promise.all([
    axios.get("/owner/stations", { params: { show_vendees_stations: true } }),
    axios.get("/owner/locations", { params: { show_vendors_locations: true } }),
    axios.get("/owner/menus"),
    axios.get("/api/getFulfillmentMethodsAndStatusNames"),
    axios.get("/api/getOwnerProperties", { params: { customer_id: customerId } }),
  ]);

  return {
    stations: stationsRes.data?.stations || [],
    locations: locationsRes.data?.locations || [],
    menus: menusRes.data?.menus || [],
    fulfillmentMethodPrettyNames: fulfillmentRes.data?.fulfillment_method_pretty_names || {},
    allowCc: ownerPropsRes.data?.owner_properties?.currency === "usd",
  };
};

export const saveDeviceProfile = (payload: DeviceProfileSavePayload) => axios.post("/admin/editDeviceProfile", payload);
